import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Avatar,
  Stack,
  AppBar,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CameraAlt as CameraIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { AppDispatch } from '../../store';
import { request } from '../../Utils/request';
import { setUser } from '../../store/slices/User';
import COLORS from '../../Utils/colors';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import { getSignedUploadUrls } from '../../services/API/Api';
import { uploadFileToSignedUrl } from '../../services/API/Api';

interface UserData {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  bio: string;
  profilePhoto?: {
    url?: string;
    fileKey?: string;
    fileType?: string;
    featured?: boolean;
  };
}

interface ApiResponse {
  success?: boolean;
  data?: any;
  message?: string;
}

const EditProfile: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const currentUser = useSelector((state: RootState) => state.user.user);
  
  const [formData, setFormData] = useState<UserData>({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    bio: '',
    profilePhoto: { 
      url: '',
      fileKey: '',
      fileType: '',
      featured: true
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [originalUsername, setOriginalUsername] = useState<string>('');
  const [usernameStatus, setUsernameStatus] = useState<string>('');
  const [usernameStatusType, setUsernameStatusType] = useState<'success' | 'error' | ''>('');
  const [isUsernameValid, setIsUsernameValid] = useState<boolean>(false);

  // Fetch latest user data when component mounts
  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser?.id || currentUser?._id) {
        const id = currentUser.id || currentUser._id;
        try {
          const response = await request<ApiResponse>(`/profile/${id}`, {
            method: 'GET',
            authorization: true,
          });

          if (response?.data) {
            // Update Redux store with latest user data
            dispatch(setUser(response.data));
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };

    fetchUserData();
  }, []); // Run only once on mount

  useEffect(() => {
    if (currentUser) {
      const username = currentUser.username || '';
      setOriginalUsername(username);
      setFormData({
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
        username: username,
        email: currentUser.email || '',
        bio: currentUser.bio || '',
        profilePhoto: currentUser.profilePhoto || { 
          url: '',
          fileKey: '',
          fileType: '',
          featured: true
        }
      });
      // Reset username validation when loading original data
      setUsernameStatus('');
      setUsernameStatusType('');
      setIsUsernameValid(true); // Original username is always valid
    }
  }, [currentUser]);

  const handleInputChange = (field: keyof UserData, value: string) => {
    // Enforce 200 character limit for bio
    if (field === 'bio' && value.length > 200) {
      value = value.slice(0, 200);
    }
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Reset username validation if username is changed back to original
    if (field === 'username') {
      if (value === originalUsername) {
        setUsernameStatus('');
        setUsernameStatusType('');
        setIsUsernameValid(true);
        setError(null); // Clear error when username is back to original
      } else {
        // Clear status when user starts typing a new username
        setUsernameStatus('');
        setUsernameStatusType('');
        setIsUsernameValid(false);
        setError(null); // Clear previous error when user starts typing
      }
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Starting profile photo upload for file:', file.name, 'Size:', file.size, 'Type:', file.type);
      
      // Step 1: Prepare file upload request payload (folder: "profile")
      const filesProp = [{
        folder: 'profile',
        fileType: String(file.type),
      }];

      console.log('Requesting signed URL with payload:', filesProp);

      // Step 2: Get signed upload URL
      const signedUrlsData = await getSignedUploadUrls(filesProp);
      console.log('Signed URL response:', signedUrlsData);
      
      const signedFiles = signedUrlsData?.data?.files || [];

      if (signedFiles.length === 0) {
        console.error('No signed files returned from API');
        setError('Could not obtain signed URL for file upload');
        return;
      }

      const { uploadUrl, fileKey } = signedFiles[0];
      console.log('Upload URL:', uploadUrl, 'File Key:', fileKey);

      if (!uploadUrl) {
        console.error('Upload URL is missing');
        setError('Invalid upload URL received');
        return;
      }

      // Step 3: Upload file to signed URL
      console.log('Uploading file to signed URL...');
      const uploadResponse = await uploadFileToSignedUrl(file, uploadUrl);
      console.log('Upload response status:', uploadResponse.status);
      
      if (uploadResponse.status === 200) {
        // Step 4: Get public URL for the uploaded file
        console.log('Requesting public URL for fileKey:', fileKey);
        const publicUrlResponse = await request<ApiResponse>('/upload/view', {
          method: 'POST',
          body: { 
            folder: "profile",
            files: [{ fileKey }] 
          },
          authorization: true,
        });

        console.log('Public URL response:', publicUrlResponse);

        if (publicUrlResponse?.data?.files?.[0]?.viewUrl) {
          const imageUrl = publicUrlResponse.data.files[0].viewUrl;
          console.log('Successfully got public URL:', imageUrl);
          setFormData(prev => ({
            ...prev,
            profilePhoto: { 
              url: imageUrl,
              fileKey: fileKey,
              fileType: file.type,
              featured: true
            }
          }));
          setSuccess('Profile picture updated successfully');
        } else {
          console.error('Invalid public URL response structure:', publicUrlResponse);
          setError('Failed to get public URL for uploaded image');
        }
      } else {
        console.error('Upload failed with status:', uploadResponse.status);
        setError(`Failed to upload profile picture (Status: ${uploadResponse.status})`);
      }
    } catch (err) {
      console.error('Image upload error:', err);
      setError('Failed to upload profile picture');
    } finally {
      setLoading(false);
    }
  };

  const checkUsernameAvailability = async (username: string): Promise<boolean> => {
    if (!username) {
      return false;
    }
    // If username is same as original, it's available
    if (username === originalUsername) {
      return true;
    }
    try {
      const res = await request<any>(`/profile/check-username/${username}`, {
        method: 'GET',
      });
      return res.data?.available === true;
    } catch (error) {
      console.error('Error checking username:', error);
      return false;
    }
  };

  const checkUsername = async (username: string) => {
    console.log(username);
    // If username is same as original, it's valid
    if (username === originalUsername) {
      setUsernameStatus('');
      setUsernameStatusType('');
      setIsUsernameValid(true);
      setError(null); // Clear error when username is original
      return;
    }
    
    if (!username) {
      setUsernameStatus('');
      setUsernameStatusType('');
      setIsUsernameValid(false);
      return;
    }
    try {
      const res = await request<any>(`/profile/check-username/${username}`, {
        method: 'GET',
      });
      console.log('res', res);
      if (res.data?.available === true) {
        setUsernameStatus(res.data.message || 'Username is available');
        setUsernameStatusType('success');
        setIsUsernameValid(true);
        setError(null); // Clear error when username is available
        console.log('username is available');
      } else {
        setUsernameStatus(res.data?.message || 'Username is not available');
        setUsernameStatusType('error');
        setIsUsernameValid(false);
        console.log('username is not available', res.data?.message);
      }
    } catch (error: any) {
      setUsernameStatus(error.message || 'Error checking username');
      setUsernameStatusType('error');
      setIsUsernameValid(false);
      console.log('error checking username');
    }
  };

  const handleSubmit = async () => {
    const id = currentUser.id || currentUser._id
    setSaving(true);
    setError(null);
    setSuccess(null);

    // Always validate username if it has changed from original
    if (formData.username !== originalUsername) {
      // Check username availability before proceeding
      const isAvailable = await checkUsernameAvailability(formData.username);
      if (!isAvailable) {
        // Update UI state to show error
        setUsernameStatus('Username is not available');
        setUsernameStatusType('error');
        setIsUsernameValid(false);
        setError('Failed to update profile');
        setSaving(false);
        return;
      }
      // Update UI state to show success and clear any previous errors
      setUsernameStatus('Username is available');
      setUsernameStatusType('success');
      setIsUsernameValid(true);
      setError(null); // Clear error when username is validated as available
    }

    try {
      const profileData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        bio: formData.bio,
        profilePhoto: formData.profilePhoto,
      };
      
      console.log('Updating profile with data:', profileData);
      
      const response = await request<ApiResponse>(`/profile/${id}`, {
        method: 'PUT',
        body: profileData,
        authorization: true,
      });

      if (response?.success) {
        // Update user data in Redux store with the response data from API
        // Use response.data if available (complete updated user object from server)
        // Otherwise fall back to merging formData with currentUser
        const updatedUser = response?.data 
          ? response.data 
          : {
              ...currentUser,
              ...formData
            };
        
        dispatch(setUser(updatedUser));
        
        setSuccess('Profile updated successfully');
        setTimeout(() => {
          navigate(-1); // Go back to previous page
        }, 1500);
      } else {
        setError(response?.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Profile update error:', err);
      setError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <TopNavBar title='Edit' canGoBack/>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Paper
          elevation={2}
          sx={{
            borderRadius: 3,
            p: 3,
            backgroundColor: 'white',
          }}
        >
          {/* Profile Picture Section */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={formData.profilePhoto?.url}
                sx={{
                  width: 120,
                  height: 120,
                  border: '3px solid #f0f0f0',
                }}
              />
              <IconButton
                component="label"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  backgroundColor: COLORS.primary,
                  color: 'white',
                  width: 36,
                  height: 36,
                  '&:hover': {
                    backgroundColor: COLORS.burntOrange,
                  },
                }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CameraIcon fontSize="small" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageUpload}
                />
              </IconButton>
            </Box>
          </Box>

          {/* Success/Error Messages */}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Form Fields */}
          <Stack spacing={3}>
            {/* Name Field */}
            <TextField
              fullWidth
              label="Name"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f9f9f9',
                  borderRadius: 2,
                },
              }}
            />

            {/* Username Field */}
            <TextField
              fullWidth
              label="User name"
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              onBlur={() => checkUsername(formData.username)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EditIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f9f9f9',
                  borderRadius: 2,
                },
              }}
            />
            {usernameStatus !== '' && (
              <Alert severity={usernameStatusType === 'success' ? 'success' : 'error'}>
                {usernameStatus}
              </Alert>
            )}

            {/* Email Field */}
            <TextField
              fullWidth
              label="Email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f9f9f9',
                  borderRadius: 2,
                },
              }}
            />

            {/* Bio Field */}
            <TextField
              fullWidth
              label="Bio"
              value={formData.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              multiline
              rows={3}
              placeholder="Tell us about yourself..."
              inputProps={{ maxLength: 200 }}
              helperText={`${formData.bio.length}/200 characters`}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f9f9f9',
                  borderRadius: 2,
                },
              }}
            />

            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={saving || !formData.firstName.trim() || !formData.username.trim() || (formData.username !== originalUsername && (usernameStatusType === 'error' || !isUsernameValid))}
              sx={{
                backgroundColor: COLORS.primary,
                color: 'white',
                borderRadius: 2,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: COLORS.burntOrange,
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: '#666',
                },
              }}
            >
              {saving ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  Updating...
                </Box>
              ) : (
                'Submit'
              )}
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default EditProfile;
